import { CSSProperties, useContext, useState } from 'react';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
  OverlayTriggerStateContext,
} from 'react-aria-components';
import { useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import CloseIcon from '@/icons/Cross.svg';
import { createBEMClasses } from '@/utils/classname';
import { checkIfTouchDevice } from '@/utils/useViewport';
import { useWebkitVirtualKeyboardHeight } from '@/utils/useWebkitVirtualKeyboardHeight';

import Stack from '../Stack';
import Paragraph from '../Typography/Paragraph';

import './Drawer.css';

const { block, element } = createBEMClasses('drawer');

interface DrawerProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => any;
  animateEntry?: boolean;
  trigger?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  closeConfirmation?: {
    title: React.ReactNode;
    content: React.ReactNode;
    confirm: React.ReactNode;
  };
  position?: 'right' | 'bottom';
}

const Drawer = ({
  isOpen,
  onOpenChange,
  animateEntry = true,
  children,
  trigger,
  style,
  className,
  closeConfirmation,
  position = 'right',
}: DrawerProps) => {
  const virtualKeyboardHeight = useWebkitVirtualKeyboardHeight();
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const isChrome = navigator.userAgent.match(/chrome|chromium|crios/i);
  const isTouchDevice = checkIfTouchDevice();

  return (
    <>
      <DialogTrigger
        isOpen={isOpen}
        onOpenChange={(opened) => {
          if (!opened && closeConfirmation) {
            setShowCloseConfirmation(true);
          } else {
            onOpenChange?.(opened);
          }
        }}
      >
        {trigger ?? (
          <AriaButton style={{ display: 'none' }}>
            {/* Warning when no trigger, so we add a hidden button */}
          </AriaButton>
        )}
        <ModalOverlay
          className={element('overlay', { 'animate-entry': animateEntry })}
          isDismissable
          style={{ '--virtual-keyboard-height': `${virtualKeyboardHeight}px` } as CSSProperties}
        >
          <Modal
            className={block({ 'animate-entry': animateEntry, position }, className)}
            style={style}
          >
            <AriaDialog>
              {/* Hide behaviour on chrome when after a button press that changes some state the first element of the dialog gets focused */}
              {isChrome && !isTouchDevice && (
                <input type="text" style={{ position: 'absolute', top: '-999px' }} />
              )}
              {children}
            </AriaDialog>
          </Modal>
        </ModalOverlay>
      </DialogTrigger>
      {closeConfirmation && (
        <ConfirmationDialog
          title={closeConfirmation.title}
          confirm={closeConfirmation.confirm}
          onConfirm={() => {
            setShowCloseConfirmation(false);
            onOpenChange?.(false);
          }}
          showDialog={showCloseConfirmation}
          setShowDialog={setShowCloseConfirmation}
        >
          <Paragraph>{closeConfirmation.content}</Paragraph>
        </ConfirmationDialog>
      )}
    </>
  );
};

export default Drawer;

export const DrawerHeader = ({
  children,
  className,
  subtitle,
}: {
  children: React.ReactNode;
  className?: string;
  subtitle?: React.ReactNode;
}) => (
  <header slot="title" className={element('header', undefined, className)}>
    <Stack row alignItems="center" justifyContent="space-between" flexWrap="nowrap">
      <div style={{ overflow: 'hidden' }}>{children}</div>
      <DrawerClose />
    </Stack>
    {subtitle}
  </header>
);

export const DrawerBody = ({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <main className={element('body', undefined, className)} style={style}>
    {children}
  </main>
);

/* TODO: to remove when all Drawer use the new footer */
export const DrawerFooter = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return <footer className={element('footer', undefined, className)}>{children}</footer>;
};

const DrawerClose = () => {
  const { t } = useLingui();
  const state = useContext(OverlayTriggerStateContext);

  return (
    <Button
      onPress={() => state?.close()}
      ariaLabel={t({ id: 'design-system.drawer.close', message: 'Close drawer' })}
      iconOnly
      size="medium"
      variant="tertiary"
      className={element('close-button')}
    >
      <CloseIcon className={element('close-icon')} />
    </Button>
  );
};
